import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {RecordUpdateRequestParams} from "../../../../../../../../core/types/records-table.types";
import {
  ButtonActionMarketingBlock,
  CreateMarketingBlockData,
  MarketingBlockData
} from "../../types/base-marketing-block-types";
import {defaultMarketingBlockData} from "../../configs/default-marketing-block-data";
import {BaseMarketingBlockPathStateService} from "../../services/base-marketing-block-path-state.service";
import {BaseMarketingBlockModalService} from "../../services/base-marketing-block-modal.service";
import {marketingBlockFormFields} from "../../configs/marketing-block-form-fields";
import {ModalsService} from "../../../../../../../../core/services/modals.service";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'div[app-mb-form]',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    CdkTextareaAutosize,
    MatIconButton,
    MatIcon
  ],
  templateUrl: './mb-form.component.html',
  styleUrl: './mb-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MbFormComponent {
  marketingBlockData = input<MarketingBlockData>(defaultMarketingBlockData);
  marketingBlockFormGroup = input<FormGroup>(new FormGroup({}));
  parentId = input<number>(0);
  isEditModeActive = input<boolean>(false);
  columnKey = input<string>('');
  noFirstData = input<boolean>(false);
  isActiveEditor = input<boolean>(false);
  updateMarketingBlock = output<MarketingBlockData>();
  deleteMarketingBlock = output<void>();
  activeEditor = output<void>();

  private baseMarketingBlockModalService: BaseMarketingBlockModalService = inject(BaseMarketingBlockModalService);
  private baseMarketingBlockPathStateService: BaseMarketingBlockPathStateService = inject(BaseMarketingBlockPathStateService);
  private modalService: ModalsService = inject(ModalsService);

  public readonly marketingBlockFormFields = marketingBlockFormFields;

  public getFormFieldClass(): string {
    if (!this.marketingBlockData().id) { return ''; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public async handleValueChange(key: string): Promise<void> {
    if (!this.marketingBlockData().id) { return; }

    const params: RecordUpdateRequestParams = {
      path: this.baseMarketingBlockPathStateService.pathState(),
      recordId: this.marketingBlockData().id,
      fieldName: key,
      value: this.marketingBlockFormGroup().get(key)?.value
    };

    const data = await this.baseMarketingBlockModalService.updatedRecordField<MarketingBlockData>(params)
      .catch(() => this.marketingBlockData());
    this.updateMarketingBlock.emit(data);
  }

  public async handleClick(key: ButtonActionMarketingBlock): Promise<void> {
    const actionMap = {
      save: () => this.createData(),
      delete: () => this.deleteMarketingBlock.emit(),
      openModal: () => this.activeEditor.emit(),
      history: () => this.openHistoryModal(),
      deleteCurrent: () => this.deleteCurrent()
    } as const;

    await actionMap[key]?.();
  }

  private async createData(): Promise<void> {
    const params: CreateMarketingBlockData = {
      marketingBlockId: this.parentId(),
      ...this.marketingBlockFormGroup().value
    };

    const data = await this.baseMarketingBlockModalService.createData(params).catch(() => defaultMarketingBlockData);
    this.updateMarketingBlock.emit(data);
  }

  private async openHistoryModal(): Promise<void> {
    this.modalService.openHistoryChangesModal({
      historyChangesData: this.marketingBlockData().history,
      columnKey: this.columnKey()
    });
  }

  private async deleteCurrent(): Promise<void> {
    await this.baseMarketingBlockModalService.deleteRecord(this.marketingBlockData().id)
      .then(() => this.deleteMarketingBlock.emit());
  }
}
